import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Button,
  Grid,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useApi } from '../api/api';
import RunOverviewCard from '../components/dispatch/RunOverviewCard';
import StepsTimeline from '../components/dispatch/StepsTimeline';
import LogsConsole from '../components/dispatch/LogsConsole';
import PayloadViewer from '../components/dispatch/PayloadViewer';
import type { DispatchRunDetail, DispatchLogEvent } from '../types/dispatch';

const DispatchRunDetailPage: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { fetchWithAuth } = useApi();
  const fetchWithAuthRef = useRef(fetchWithAuth);
  fetchWithAuthRef.current = fetchWithAuth;

  const [data, setData] = useState<DispatchRunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [boardsMap, setBoardsMap] = useState<Record<number, string>>({});
  const [levelFilter, setLevelFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const fetchStepLogs = useCallback(
    async (rId: string, stepId: number) => {
      const res = await fetchWithAuthRef.current(
        `/dispatch/runs/${rId}/steps/${stepId}/logs`
      );
      return (res as { events: DispatchLogEvent[] }).events || [];
    },
    []
  );

  // Fetch boards once on mount
  useEffect(() => {
    let cancelled = false;
    const doFetch = async () => {
      try {
        const res = await fetchWithAuthRef.current('/api/boards?page=1');
        if (cancelled) return;
        const boardsList = res.boards || [];
        const map: Record<number, string> = {};
        boardsList.forEach((b: { id: number; name: string }) => {
          map[b.id] = b.name;
        });
        setBoardsMap(map);
      } catch {
        if (!cancelled) setBoardsMap({});
      }
    };
    doFetch();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch run detail when runId changes
  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchWithAuthRef.current(`/dispatch/runs/${runId}`)
      .then((res: DispatchRunDetail) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load run');
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  if (!runId) {
    return (
      <Container>
        <Alert severity="error">Missing run ID</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container>
        <Alert severity="error">{error || 'Run not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back
        </Button>
      </Container>
    );
  }

  const boardName = data.run.board_id
    ? boardsMap[data.run.board_id] ?? `#${data.run.board_id}`
    : undefined;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dispatch-control-center')}
        >
          Back
        </Button>
        <Typography variant="h5" fontWeight={600}>
          Run Detail
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <RunOverviewCard run={data.run} boardName={boardName} />
        </Grid>

        <Grid item xs={12} md={6}>
          <StepsTimeline
            steps={data.steps}
            runId={runId}
            logs={data.logs}
            fetchStepLogs={fetchStepLogs}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <LogsConsole
            logs={data.logs}
            levelFilter={levelFilter}
            searchFilter={searchFilter}
            onLevelChange={setLevelFilter}
            onSearchChange={setSearchFilter}
          />
        </Grid>

        <Grid item xs={12}>
          <PayloadViewer payload={data.run.payload ?? null} />
        </Grid>
      </Grid>
    </Container>
  );
};

export default DispatchRunDetailPage;
